import { Bouton } from '../ui/composants/bouton'
import { ReceiptEuro } from 'lucide-react'
import { EnteteSection } from '../ui/composants/entete-section'
import { useLiveQuery } from 'dexie-react-hooks'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { lireEntreprise } from '../lib/entreprise'
import { feuilleImpayes, totauxImpayes, type Impaye } from '../lib/impayes'
import { versCsv } from '../lib/tableur/export'
import { csvBlob, telecharger } from '../lib/tableur/tableur'
import { formatEuro } from '../lib/money'
import { LIBELLES_NIVEAU, lireRelances, type Relance } from '../lib/relance'
import { getBase } from '../outbox/base'
import { lister } from '../outbox/depot'
import { useImpayes } from '../ui/impayes'
import { RelanceImpaye } from './RelanceImpaye'
import { BarreActions } from '../ui/synchro'

const NATURE: Record<Impaye['type'], string> = { facture: 'Facture', acompte: 'Facture d’acompte', situation: 'Situation' }
const dateFr = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR')

/**
 * Ce qu'il reste à encaisser : factures, acomptes et situations émis, non annulés, pas encore marqués payés,
 * les plus en retard d'abord. L'échéance est celle des factures électroniques (émission + 30 jours).
 */
export default function Impayes() {
  const tous = useImpayes()
  const [retardSeul, setRetardSeul] = useState(false)
  const [aRelancer, setARelancer] = useState<Impaye | null>(null)
  const base = getBase()
  const clients = useLiveQuery(() => lister(base, 'clients'), [], [])
  const entreprise = useLiveQuery(async () => (await lireEntreprise(base)) ?? null, [])
  const relances = useLiveQuery(() => lireRelances(base), [], {} as Record<string, Relance[]>)
  if (tous === undefined) return <main className="flex-1 p-4" />
  const t = totauxImpayes(tous)
  const liste = retardSeul ? tous.filter((i) => i.joursRetard > 0) : tous

  return (
    <>
      <main className="flex flex-1 flex-col gap-4 p-4">
        <EnteteSection
          Icone={ReceiptEuro}
          titre="Impayés"
          description={
            <>
              Factures, acomptes et situations émis et pas encore marqués payés (retenue de garantie exclue).{' '}
              <Link to="/" className="underline">
                Retour
              </Link>
            </>
          }
        />

        <section className="rounded-2xl bg-slate-100 p-3" aria-label="Totaux">
          <p className="flex justify-between font-bold">
            <span>À encaisser ({t.nombre})</span>
            <span>{formatEuro(t.total)}</span>
          </p>
          <p className={`flex justify-between ${t.nombreRetard ? 'font-semibold text-red-800' : 'text-slate-700'}`}>
            <span>dont en retard ({t.nombreRetard})</span>
            <span>{formatEuro(t.retard)}</span>
          </p>
        </section>

        {t.nombreRetard > 0 && (
          <label className="flex min-h-12 items-center gap-3">
            <input type="checkbox" className="size-6" checked={retardSeul} onChange={(e) => setRetardSeul(e.target.checked)} />
            <span>Seulement ceux en retard</span>
          </label>
        )}

        {liste.length === 0 && <p className="text-slate-700">{tous.length === 0 ? 'Rien à encaisser : tout ce qui a été émis est payé.' : 'Aucun impayé en retard.'}</p>}
        <ul className="flex flex-col gap-2">
          {liste.map((i) => (
            <li key={i.id}>
              <Link to={i.lien} className={`flex min-h-12 items-center justify-between gap-3 rounded border p-3 ${i.joursRetard > 0 ? 'border-red-700' : 'border-slate-300'}`}>
                <span className="min-w-0">
                  <span className="block truncate font-semibold">{i.clientNom}</span>
                  <span className="block text-sm text-slate-700">
                    {NATURE[i.type]} {i.numero} · émise le {dateFr(i.emisLe)}
                  </span>
                  <span className={`block text-sm ${i.joursRetard > 0 ? 'font-semibold text-red-800' : 'text-slate-700'}`}>
                    {i.joursRetard > 0 ? `En retard de ${i.joursRetard} jour${i.joursRetard > 1 ? 's' : ''} (échéance le ${dateFr(i.echeance)})` : `Échéance le ${dateFr(i.echeance)}`}
                  </span>
                </span>
                <span className="shrink-0 font-semibold">{formatEuro(i.montant)}</span>
              </Link>
              {i.joursRetard > 0 && (
                <div className="flex items-center justify-between gap-3 px-1 pt-1 text-sm">
                  <span className="text-slate-700">
                    {relances[i.id]?.length
                      ? `Relancé le ${new Date(relances[i.id].at(-1)!.date).toLocaleDateString('fr-FR')} (${LIBELLES_NIVEAU[relances[i.id].at(-1)!.niveau].toLowerCase()}${relances[i.id].length > 1 ? `, ${relances[i.id].length} fois` : ''})`
                      : 'Jamais relancé'}
                  </span>
                  <Bouton variante="secondaire" onClick={() => setARelancer(i)}>
                    Relancer
                  </Bouton>
                </div>
              )}
            </li>
          ))}
        </ul>
      </main>
      {aRelancer && (
        <RelanceImpaye
          impaye={aRelancer}
          client={clients.find((c) => c.id === aRelancer.clientId)}
          entreprise={entreprise ?? undefined}
          relances={relances[aRelancer.id] ?? []}
          fermer={() => setARelancer(null)}
        />
      )}
      <BarreActions>
        {tous.length > 0 && (
          <Bouton variante="secondaire" onClick={() => telecharger(csvBlob(versCsv(feuilleImpayes(liste))), `impayes-${new Date().toISOString().slice(0, 10)}.csv`)}>
            Exporter (CSV)
          </Bouton>
        )}
        <Bouton asChild variante="secondaire">
          <Link to="/rapprochement">Relevé bancaire</Link>
        </Bouton>
        <Bouton asChild variante="secondaire" className="flex-1">
          <Link to="/">
            Accueil
          </Link>
        </Bouton>
      </BarreActions>
    </>
  )
}
